import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {NavigationExtras, Router} from '@angular/router';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { forkJoin } from 'rxjs';
import { User } from '../../shared/classes/user';
import { Utilities } from '../../shared/utilities';
import { faFacebookF, faTwitter, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { Settings } from '../../shared/settings';
import { UserService } from '../../shared/services/user.service';

@Component({
  selector: 'app-sign-in-form',
  templateUrl: './sign-in-form.component.html',
  styleUrls: ['./sign-in-form.component.css']
})

export class SignInFormComponent implements OnInit, OnDestroy {
  // Font Awesome icons
  public faFacebookF = faFacebookF;
  public faTwitter = faTwitter;
  public faGoogle = faGoogle;

  @Input() isInPopup = false;
  @Output() changeScreen: EventEmitter<any> = new EventEmitter<any>();
  @Input() message = '';
  public processing = false;

  public formData = {
    email: '',
    password: '',
    error: '',
    message: ''
  };

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private userService: UserService
  ) {
  }

  ngOnInit(): void {
    const that = this;
    that.resetFormData();
    if (that.isInPopup && that.message) {
      that.formData.message = that.message;
    }
    // Si un usuario logueado entra al login por URL, cierro sesión (vuelve a la home).
    if (!that.isInPopup && that.authenticationService.userIsLoggedIn()) {
      that.authenticationService.logout();
    }
  }

  ngOnDestroy(): void {
  }

  public login(): void {
    const that = this;
    if (!that.processing) {
      that.processing = true;
      if (that.validateData()) {
        forkJoin([
          that.authenticationService.login(that.formData.email, that.formData.password)
        ]).subscribe(([loginResponse]) => {
            if (loginResponse) {
              if (!loginResponse.HasError && loginResponse.UsuarioData) {
                that.resetFormData();
                const loggedInUser = User.parse(loginResponse.UsuarioData);
                const accessToken = loginResponse.UsuarioData.AccessToken;
                localStorage.setItem('APPTenfieldPlayerAccessToken', accessToken);
                that.authenticationService.loggedInUser = Object.assign({}, loggedInUser);
                localStorage.setItem('APPTenfieldPlayerLoggedInUser', JSON.stringify(loggedInUser));
                that.resetFormData();
                if (that.isInPopup) {
                  window.location.reload();
                } else {
                  const redirectTo = localStorage.getItem('APPTenfieldPlayerRedirectAfterLogin');
                  localStorage.removeItem('APPTenfieldPlayerRedirectAfterLogin');
                  if (redirectTo) {
                    const redirectToAsJSON = JSON.parse(redirectTo);
                    if (redirectToAsJSON.route) {
                      const navigationExtras: NavigationExtras = {
                        queryParams: redirectToAsJSON.params
                      };
                      that.router.navigate([redirectToAsJSON.route], navigationExtras);
                    } else {
                      that.router.navigate(['']);
                    }
                  } else {
                    that.router.navigate(['']);
                  }
                }
              } else {
                if (loginResponse.ErrorMessage) {
                  that.formData.error = `<p><b>¡Error!</b></p><p>${loginResponse.ErrorMessage}</p>`;
                } else {
                  that.formData.error = '<p>Ha ocurrido un error, por favor, intente nuevamente.</p>';
                }
              }
            } else {
              that.formData.error = '<p><b>Ha ocurrido un error.</b></p><p>Por favor, intente nuevamente.</p>';
            }
            that.processing = false;
          },
          (error) => {
            Utilities.logError('Error al realizar el login');
            Utilities.logError(error);
            that.processing = false;
            localStorage.clear();
            that.formData.error = '<p><b>Ha ocurrido un error.</b></p><p>Por favor, intente nuevamente.</p>';
          });
      } else {
        that.processing = false;
      }
    }
  }

  public validateData(): boolean {
    const that = this;
    that.formData.error = '';
    that.formData.message = '';
    let dataIsValid = true;
    if (
      that.formData.email &&
      that.formData.email.trim().length > 0 &&
      that.formData.password &&
      that.formData.password.trim().length > 0
    ) {
      that.formData.email = that.formData.email.trim();
      that.formData.password = that.formData.password.trim();
      if (!that.formData.email.match(Utilities.EXP_REG_FORMATO_EMAIL)) {
        dataIsValid = false;
        that.formData.error = '<p><b>Verifique los datos.</b></p><p>El email no tiene un formato válido.</p>';
      }
    } else {
      dataIsValid = false;
      that.formData.error = '<p><b>Complete el formulario.</b></p><p>Todos los campos son obligatorios.</p>';
    }
    return dataIsValid;
  }

  private resetFormData(): void {
    this.formData = {
      email: '',
      password: '',
      error: '',
      message: ''
    };
  }

  public goToSignUp(): void {
    if (this.isInPopup) {
      this.changeScreen.emit({screen: 'SIGN_UP'});
      // window.open(Settings.signUpURL, '_blank').focus();
    } else {
      this.goTo('registro');
    }
  }

  public forgotPassword(): void {
    if (this.isInPopup) {
      this.changeScreen.emit({screen: 'FORGOT_PASSWORD'});
    } else {
      this.goTo('olvido-password');
    }
  }

  public goTo(url): void {
    this.router.navigate([url]);
  }

  public externalLogin(socialMedia: string): void {
    // window.location.href = Settings.externalLoginURL + socialMedia;
    const that = this;
    const win = window.open(Settings.externalLoginURL + socialMedia, '_blank', 'height=500,width=500');
    const REDIRECT = Settings.backendRedirectURL; // 'http://localhost:4200/backend-callback';
    const pollTimer = window.setInterval(() => {
      try {
        if (win.document.URL.indexOf(REDIRECT) !== -1) {
          window.clearInterval(pollTimer);
          const url = win.document.URL;
          const accessToken = that.gup(url, 'accesstoken');
          win.close();
          that.validateToken(accessToken);
        }
      } catch (e) {
      }
    }, 100);
  }

  // credits: http://www.netlobo.com/url_query_string_javascript.html
  public gup(url: string, name: string): string {
    name = name.replace(/[\[]/, '\\\[').replace(/[\]]/, '\\\]');
    const regexS = '[\\#&]' + name + '=([^&#]*)';
    const regex = new RegExp(regexS);
    const results = regex.exec(url);
    if (results === null) {
      return '';
    } else {
      return results[1];
    }
  }

  public validateToken(acToken): void {
    const that = this;
    localStorage.setItem('APPTenfieldPlayerAccessToken', acToken);
    forkJoin([
      that.userService.getUserData()
    ]).subscribe(([getUserDataResponse]) => {
        if (getUserDataResponse) {
          if (!getUserDataResponse.HasError && getUserDataResponse.UsuarioData) {
            const loggedInUser = User.parse(getUserDataResponse.UsuarioData);
            that.authenticationService.loggedInUser = Object.assign({}, loggedInUser);
            localStorage.setItem('APPTenfieldPlayerLoggedInUser', JSON.stringify(loggedInUser));
            // tslint:disable-next-line:max-line-length
            if (that.isInPopup) {
              window.location.reload();
            } else {
              const redirectTo = localStorage.getItem('APPTenfieldPlayerRedirectAfterLogin');
              localStorage.removeItem('APPTenfieldPlayerRedirectAfterLogin');
              if (redirectTo) {
                const redirectToAsJSON = JSON.parse(redirectTo);
                if (redirectToAsJSON.route) {
                  const navigationExtras: NavigationExtras = {
                    queryParams: redirectToAsJSON.params
                  };
                  that.router.navigate([redirectToAsJSON.route], navigationExtras);
                } else {
                  that.router.navigate(['']);
                }
              } else {
                that.router.navigate(['']);
              }
            }
          }
        } else {
        }
      },
      (error) => {
        Utilities.logError('Error al obtener la información del usuario logueado');
        Utilities.logError(error);
        localStorage.clear();
      });
  }
}
